import {
  Component, EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit, Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  AfterViewInit
} from '@angular/core';
import {PrimengTableCols} from '@sharedcommon/interfaces/primeng-table-cols';
import {RawMaterialDto} from '@sharedrest/data.restservice';
import {Rights} from '@sharedcommon/util/rights.util';
import {PrimengTableService} from '@sharedcommon/components/primeng-table/primeng-table.service';
import {ActionType, ExtendedRowActionModel, PrimengRowActionModel} from '@sharedcommon/model/primeng-row-action.model';
import {UserDto} from '@sharedrest/identity.restservice';
import {PrimengCheckColumnModel} from '@sharedcommon/model/primeng-check-column.model';
import {PrimengTableVariableRequirementModel} from '@sharedcommon/model/primeng-table-variable-requirement.model';
import {SortEvent, SortMeta} from 'primeng/api';
import {Table} from 'primeng/table';
import {Observable, Subject} from 'rxjs';
import {PrimengLazyEventModel} from '@sharedcommon/model/primeng-lazy-event.model';
import {UserService} from '@sharedcommon/services/user.service';
import { Constants } from '@sharedcommon/util/constants.util';
import { RawMaterialOrSampleUtil } from '@sharedcommon/util/rawmaterial-or-sample.util';
import { AbstractControl } from '@angular/forms';
import { FunctionUtils } from '@sharedcommon/util/functions.util';
import {takeUntil} from "rxjs/operators";
import { NeqDialogService } from '@sharedcommon/services/dialog.service';
import { URN } from '@sharedcommon/model/urn.model';
import { RawMaterialDtoExtend } from '@root/user/src/app/view/neq/pages/rawmaterials/rawmaterials-list.page';

const editAction = 'edit';
const enableAction = 'enable';
const equationAction = 'equation';
const goUpAction = 'goUp';
const goDownAction = 'goDown';
const nutrientAction = 'nutrient';
const valueAction = 'value';
const disableAction = 'disable';
const archiveAction = 'archive';
const desarchiveAction = 'desarchive';
const deleteAction = 'delete';
const duplicateAction = 'duplicate';
const extendAction = 'extend';

@Component({
  selector: 'neq-primeng-table',
  templateUrl: './primeng-table.component.html',
  styleUrls: ['./primeng-table.component.scss']
})
export class PrimengTableComponent implements OnChanges, OnInit, OnDestroy, AfterViewInit {

  /**
   * Get the object table from the primeng table
   *
   * @private
   */
  @ViewChildren(Table) private readonly table!: QueryList<Table>;

  /**
   * Subject to trigger the unsubscribes
   */
  destroy$ = new Subject();

  /**
   * Edit action identifier
   */
  static readonly editAction = editAction;
  get staticEditAction() {
    return PrimengTableComponent.editAction;
  }


  /**
   * Enable action identifier
   */
  static readonly enableAction = enableAction;
  get staticEnableAction() {
    return PrimengTableComponent.enableAction;
  }

  /**
   * Equation action identifier
   */
  static readonly equationAction = equationAction;
  get staticEquationAction() {
    return PrimengTableComponent.equationAction;
  }

  /**
   * Go up action identifier
   */
  static readonly goUpAction = goUpAction;
  get staticGoUpAction() {
    return PrimengTableComponent.goUpAction;
  }

  /**
   * Go down action identifier
   */
  static readonly goDownAction = goDownAction;
  get staticGoDownAction() {
    return PrimengTableComponent.goDownAction;
  }

  /**
   * Nutrient action identifier
   */
  static readonly nutrientAction = nutrientAction;
  get staticNutrientAction() {
    return PrimengTableComponent.nutrientAction;
  }

  /**
   * Value action identifier
   */
  static readonly valueAction = valueAction;
  get staticValueAction() {
    return PrimengTableComponent.valueAction;
  }

  /**
   * disable action identifier
   */
  static readonly disableAction = disableAction;
  get staticDisableAction() {
    return PrimengTableComponent.disableAction;
  }

  /**
   * archive action identifier
   */
  static readonly archiveAction = archiveAction;
  get staticArchiveAction() {
    return PrimengTableComponent.archiveAction;
  }

  /**
   * desarchive action identifier
   */
  static readonly desarchiveAction = desarchiveAction;
  get staticDesarchiveAction() {
    return PrimengTableComponent.desarchiveAction;
  }

  /**
   * delete action identifier
   */
  static readonly deleteAction = deleteAction;
  get staticDeleteAction() {
    return PrimengTableComponent.deleteAction;
  }

  /**
   * delete action identifier
   */
  static readonly duplicateAction = duplicateAction;
  get staticDuplicateAction() {
    return PrimengTableComponent.duplicateAction;
  }

  /**
   * Extend licence action identifier
   */
  static readonly extendAction = extendAction;
  get staticExtendAction() {
    return PrimengTableComponent.extendAction;
  }

  /**
   * Right for the actions on a nutrient of the user app
   */
  deleteRight = [Rights.U_MP_DELETE];
  enableDisableRight = [Rights.U_MP_ENABLE_DISABLE];
  archiveRight = [Rights.U_MP_ARCHIVE];
  desarchiveRight = [Rights.U_MP_DESARCHIVE];
  duplicateRight = [ Rights.U_RM_DUP ] ;

  /**
   * Right for the actions on a nutrient of the admin app
   */
  adminRight = {
    edit: [],
    equation: [],
    nutrient: [],
    value: [],
    delete: []
  };

  /**
   * All requirement for the Variable column functionality
   */
  @Input() dynamicRequirements: PrimengTableVariableRequirementModel;

  /**
   * At true if you do not want any pagination and load all
   */
  @Input() noPagination: boolean ;

  /**
   *
   */
  @Input() nutrients: {} = null;

  /**
   * Active or not the filter on header of the table
   */
  @Input() activeFilter = false;

  /**
   * Column configuration of the primeNg table
   */
  @Input() cols: PrimengTableCols[];

  /**
   * Data of the primeng Table
   */
  @Input() dataRows: any[];

  /**
   * Enable or disable the row selection
   */
  @Input() selectableRows = false;

  /**
   * Function to enable/disable the checkbox present if a selectable row
   */
  @Input() disableSelectableCell: (rowData: any) => boolean ;

  /**
   * List of action the current user can perform
   */
  @Input() actions: 'user' | PrimengRowActionModel[];

  /**
   * Unique session Id to manage the store of filter/pagination/sort
   */
  @Input() sessionId: string;

  /**
   * Total number of rows of the table
   */
  @Input() totalRecords = 0;

  /**
   * Manage the loading mode of the table
   */
  @Input() loading: boolean;

  /**
   * the function which return the row class
   */
  @Input() rowClass: (data: any) => string ;

  /**
   * Emitter of the state of the table: Sort Filters and Pagination
   */
  @Output() loadDataEmitter = new EventEmitter<PrimengLazyEventModel>();

  /**
   * Set or not the lazy mode on the table
   * The lazy mode apply the filter, sort and pagination on backend API
   */
  @Input() lazy = true;

  /**
   * Number of dynamic column present
   */
  @Input() numberDynamicColumn = 0;

  /**
   * Event when code/entity unicity checked
   */
  @Input() eventDataChecked: Observable<void>;

  /**
   * Initial RawMaterial Data
   */
  @Input() initalRawMaterialsData: RawMaterialDtoExtend[];

  /**
   * Nutrients data for RawMaterial
   */
  @Input() nutrientsData: any[];

  /**
   * Initial nutrients data for RawMaterial
   */
  @Input() initNutrientsData: any[];

  /**
   * Map of the filter activated or not
   */
  filterMap: Map<string, {active: boolean}>;

  /**
   * List of row selected with the checkBox
   */
  selectedRows: any[] = [];

  /**
   * Starting row of the list
   */
  firstRow = (this.lazy) ? 0 : 1;

  /**
   * Store value for the multi sort
   */
  multiSortMeta: SortMeta[] = null;

  /**
   * The number of element to load for each page
   */
  rows: number = Constants.DEFAULT_NB_ROW_BY_PAGE;

  /**
   * The page selection option
   */
  rowsPerPageOptions: number[] = Constants.ARRAY_NB_ROW_BY_PAGE;

  constructor(
    private readonly userService: UserService,
    private readonly primengTableService: PrimengTableService,
    private readonly dialogService: NeqDialogService
  ) {
  }

  ngOnInit() {
    this.restoreDefaultSettings() ;
    // Subscribe to the reset table event
    this.primengTableService.resteTableEvent$.pipe(takeUntil(this.destroy$)).subscribe((resetString: string) => {
      if (resetString) {
        // Reset the state of the table
        this.table.last.clearState();
        // Reset the selected row
        this.selectedRows = [];
        this.primengTableService.setSelectionRowEvent(this.selectedRows);
        this.primengTableService.setResetTableEvent(null);
      }
    })
  }

  ngAfterViewInit() {
  }

  ngOnDestroy() {
    this.destroy$.next(null);
  }

  getStateKey(): string {
    return PrimengTableService.getTableStateKey(this.sessionId) ;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.actions) {
      this.getRightForAction();
    }

    if (changes?.cols && this.cols) {
      this.constructDefaultSort();
    }

    if (changes?.dataRows && this.lazy === false && this.dataRows && this.dataRows.length > 0) {
      this.totalRecords = this.dataRows.length
    }
  }

  /**
   * Construct the map of filter activation from the list of cols pass to the component
   */
  constructMapFilter(): void {
    if (this.cols) {
     this.filterMap = new Map<string, {active: boolean}>();
     this.cols.forEach((col: PrimengTableCols) => {
       this.filterMap.set(col.field, {active: false});
     });
    }
  }

  /**
   * Return if the col type is of checkbox type
   *
   * @param colType
   */
  centerCheckBox(colType: string): boolean {
    return  colType ===  'calculatedCheckbox' ||
    colType === 'input-refCalValue-checkbox' ||
    colType === 'input-refValue-checkbox' ||
    colType === 'checkCell' ||
    colType === 'checkCellMatrixRight' ||
    colType === 'checkedCell';
  }

  /**
   * Show the filter input for the field colId
   *
   * @param colId id of the column
   */
  activateFilter(colId: string) {
    this.filterMap.get(colId).active = true;
  }

  /**
   * Hide the filter input for the field colId
   *
   * @param colId index of the column
   */
  disableFilter(colId: string) {
    this.filterMap.get(colId).active = false;
  }

  /**
   * Function launch when one of more rows are selected or unselected and emit the selected row
   */
  onRowSelectionUpdate() {
    /* Keep the selected row where the selection is enabled
     * Use this to avoid to select row while its selection is disabled
     * it can be the case when click on the header checkbox component p-tableHeaderCheckbox */
    const filteredSelectedRow = this.selectedRows.filter((row) => {
      return !this.disableSelectableCheckboxCell(row) ;
    }) ;
    this.primengTableService.setSelectionRowEvent(filteredSelectedRow);
  }

  /**
   * Returns the desired action object for a given type
   *
   * @param actionTypeString type of action to return
   */
  getRightAction(actionTypeString: ActionType): PrimengRowActionModel {
    if (this.action) {
      const actions = this.actions as PrimengRowActionModel[];
      return actions.find((action: PrimengRowActionModel) => action.type === actionTypeString);
    }
    return null;
  }

  /**
   * Function which restores default settings
   * Reset the row selection (check box user in p-tableCheckbox) in the localStorage
   * and reset the default number of rows per page
   */
  restoreDefaultSettings(): void {
      const key = this.getStateKey();

      // manual choice by user
      const explicitUserRows = localStorage.getItem(key + '_explicit_rows');
      // cached preference
      const cachedMetaRows = localStorage.getItem(key + '_cached_meta_rows');

      if (explicitUserRows) {
        this.rows = +explicitUserRows;
      } else if (cachedMetaRows) {
        this.rows = +cachedMetaRows;
        this.setDefaultRowsParam();
      } else {
        this.rows = Constants.DEFAULT_NB_ROW_BY_PAGE;
        this.setDefaultRowsParam();
      }

      this.patchPrimeNgStateRows(key, this.rows, true);

      this.selectedRows = [];
      this.onRowSelectionUpdate();
    }

    private patchPrimeNgStateRows(key: string, newRows: number, clearSelection: boolean): void {
      const stored = localStorage.getItem(key);
      let state: any = {};

      if (stored) {
        try { state = JSON.parse(stored); } catch {}
      }

      if (clearSelection) {
        state.selection = [];
      }

      if (state.first === undefined || state.first === null || isNaN(state.first)) {
        state.first = 0;
      }

      state.rows = newRows;
      localStorage.setItem(key, JSON.stringify(state));
    }

    /**
     * Set the default rows number
     */
    setDefaultRowsParam(): void {
      this.userService.getMetasObservable()
        .pipe(takeUntil(this.destroy$))
        .subscribe(metas => {
          if (metas) {
            const meta = metas.find(x => x.key === Constants.USER_META_NB_ROW_BY_PAGE);
            if (meta && meta.value) {
              const key = this.getStateKey();
              const metaRows = +meta.value;

              localStorage.setItem(key + '_cached_meta_rows', metaRows.toString());

              if (!localStorage.getItem(key + '_explicit_rows')) {
                if (this.rows !== metaRows) {
                  this.rows = metaRows;
                  this.patchPrimeNgStateRows(key, this.rows, false);

                  // update PrimeNG
                  if (this.table && this.table.last) {
                    this.table.last.rows = this.rows;
                    this.table.last.saveState();
                  }
                }
              }
            }
          }
        });
    }

    /**
     * Sync Angular var to live user choice
     */
    onPageChange(event: any) {
      if (event && event.rows !== undefined) {
        this.rows = event.rows;
        //Save manual change in pagination
        localStorage.setItem(this.getStateKey() + '_explicit_rows', this.rows.toString());
      }
      this.broadCastFirstPage();
    }

    /**
     * Emit the new state of the table : Sort, Filters and Pagination
     */
    loadData(event: PrimengLazyEventModel) {
      if (event && event.rows !== undefined) {
        this.rows = event.rows;
      }
      this.loadDataEmitter.emit(event);
    }

  /**
   * Return if a row is enabled or not
   *
   * @param rawmaterialDto line of the table
   */
  isEnabled(rawmaterialDto: RawMaterialDto) {
    return RawMaterialOrSampleUtil.isEnabled(rawmaterialDto);
  }

  /**
   * Return if a raw is archived or not
   *
   * @param rawmaterialDto Line of the table
   */
  isArchived(rawmaterialDto: RawMaterialDto) {
    return RawMaterialOrSampleUtil.isArchived(rawmaterialDto);
  }

  /**
   * Return if a row can be enable or not
   *
   * @param rawmaterialDto Line of the table
   */
  canBeEnabled(rawmaterialDto: RawMaterialDto) {
    return RawMaterialOrSampleUtil.canBeEnabled(rawmaterialDto);
  }

  /**
   * Determine if a row can be deleted
   *
   * @param rawmaterialDto Line of the table
   */
  canBeDeleted(rawmaterialDto: RawMaterialDto) {
    return RawMaterialOrSampleUtil.canBeDeleted(rawmaterialDto);
  }

  /**
   * Determine if a row can be duplicated or exported
   *
   * @param rawmaterialDto Line of the table
   */
  canBeDuplicated(rawMaterialDto: RawMaterialDto) {
    return !this.isArchived(rawMaterialDto) && this.isEnabled(rawMaterialDto);
  }

  /**
   * Determine if the current user is super admin or not
   *
   * @param user User information
   */
  isSuperAdmin(user: UserDto): boolean {
    return user.admin;
  }

  /**
   * Determine if the index passed is first of the table
   *
   * @param index index of the current line
   */
  isFirst(index: number): boolean {
    return index === 0;
  }

  /**
   * Determine if the index passed is last of the table
   *
   * @param index index of the current line
   */
  isLast(index: number): boolean {
    return index === this.dataRows.length - 1;
  }

  /**
   * Return the right class to define the position of the data in the column
   *
   * @param position
   */
  returnCellDataPositionClass(position: 'left' | 'right' | 'center'): string {
    if (position === 'center') {
      return 'primeng-table-center-cell';
    } else if (position === 'right')  {
      return 'primeng-table-right-cell';
    } else {
      return 'primeng-table-left-cell';
    }
  }

  /**
   * return the row class
   * @param data the data of the row
   * @returns the corresponding class for the row
   */
  returnRowClass(data: any): string {
    return this.rowClass?.(data) || '' ;
  }

  /**
   * Show or not the checkbox for the rawmaterials input page
   */
  showCheckBox(row: any) {
    if (this.nutrients !== null) {
      return this.nutrients?.[row.nutrientCode]?.proposed !== null;
    }
    return true;
  }

  disableSelectableCheckboxCell(rowData: any): boolean {
    return this.disableSelectableCell?.(rowData) ;
  }

  showCol(col: PrimengTableCols): boolean {
    return (col.show === undefined || col.show) && (col.header !== '' || this.hasAllDynamicValue()) ;
  }

  /**
   * Check if the col has an input inside
   * @param col the col to check
   * @returns true if the col includes input
   */
  isColForInput(col: PrimengTableCols): boolean {
    return col.type?.includes('input') && !col.isRawMaterialEditColumn;
  }

  /**
   * Check if the col is for field
   * @param col the col to check
   * @returns true if the col is for field
   */
  isColForFieldOnly(col: PrimengTableCols): boolean {
    return !col.type && !col.isRawMaterialEditColumn;
  }

  /**
   * Check if the col is for extended action
   * @param col the col to check
   * @returns true if the col is for extended action
   */
  isColForExtendedAction(col: PrimengTableCols): boolean {
    return col?.type === "extendedActions" && !col.isRawMaterialEditColumn;
  }

  /**
   * Check if the col is handled by PrimengTableCellTypesComponent component
   * @param col the col to check
   * @returns true if the col is handled by PrimengTableCellTypesComponent
   */
  isColForOtherTypes(col: PrimengTableCols): boolean {
    return !this.isColForExtendedAction(col) && !this.isColForFieldOnly(col) && !this.isColForInput(col) && !col.isRawMaterialEditColumn;
  }

  /**
   * Check if the col is a RawMaterials edition page
   * @param col the col to check
   * @returns true if it's a RawMaterial mass edit column
   */
  isColForRawMaterialsEdit(col: PrimengTableCols): boolean {
    return col.isRawMaterialEditColumn;
  }

  /**
   * Check if the action button has to be shown
   * @param rowData the row data from what the action is linked
   * @param action the action of the button
   * @returns true if the action has to be shown
   */
  showExtendedAction(rowData: any, actionRowModel: ExtendedRowActionModel): boolean {
    return (!actionRowModel.right || this.userService.hasRights(actionRowModel.right))
              && (!actionRowModel.showAction || actionRowModel.showAction(rowData));
  }

  /**
   * Check if the action button has to be disabled
   *
   * @param rowData the row data from what the action is linked
   * @param action  the action of the button
   * @returns true if the action has to be disabled
   */
  disableExtendedAction(rowData: any, actionRowModel: ExtendedRowActionModel): boolean {
    return !!actionRowModel.disableAction && actionRowModel.disableAction(rowData);
  }

  /**
   * Call the event emitter to check all the cell for the column
   * @param clickEvent the click event
   * @param col the column from what the check box is done
   */
  changeAllCheckboxesState(clickEvent: any, col: PrimengTableCols): void {
    const checkEvent: any = {} ;
    checkEvent.checked = !this.areAllCheckboxesChecked(col);
    this.primengTableService.setCheckAllCellEvent({checkEvent: checkEvent, row: null, field: col.field});
    /* Stop the propagation of event to do not trig the sorting process which is in the header */
    clickEvent.stopPropagation();
  }

  /**
   * Check if all the line for the column 'field' are checked
   *
   * @param field id of the column
   */
  areAllCheckboxesChecked(col: PrimengTableCols): boolean {
    if(col.field == 'overwritten' || col.field == 'imported'){
      return col.importPreview?.predictionModels && col.checkOutsideNgModel && col.checkOutsideNgModel.length > 0
          && col.checkOutsideNgModel.length === col.importPreview.predictionModels.length;
    }
    else{
      return this.dataRows?.every(e => this.isChecked(e, col.field));
    }
  }

  /**
   * Check if the column 'field' of the current line is checked
   *
   * @param row line of the table
   * @param field id of the column
   */
  isChecked(row: any, field: string): boolean {
    return row?.[field];
  }

  /**
   * Emit the enable action for the current row
   *
   * @param row current row in the table
   * @param type Type of the action
   */
  action(row: any, type: string) {
    this.primengTableService.setActionEvent({type, row});
  }

  /**
   * Emit the event of end of cell editing
   *
   * @param editEvent edition information
   */
  emitInputCellEditFinish(editEvent: {data: any, field: any}) {
    this.primengTableService.setEditCellEvent({field: editEvent.field.field, rowData: editEvent.data});
  }

  /**
   * Get all the right from the actions array
   */
  getRightForAction(): void {
    if (this.actions && this.actions !== 'user') {
      const actions = this.actions;
      actions.forEach((action: PrimengRowActionModel) => {
        if (action?.right) {
          this.adminRight[action.type] = action.right;
        }
      });
    }
  }

  /**
   * Check if all the dynamic value are initialised
   */
  hasAllDynamicValue(): boolean {
    return PrimengTableService.haveAllDynamicValue(this.dynamicRequirements);
  }

  /**
   * Return the right tooltip translated string from variables
   *
   * @param dynamicValue dynamic value passed to the column object
   * @param tableType Type of the table (variable or model)
   */
  getHeaderTooltip(dynamicValue: any, tableType: string) {
    if (tableType === 'variable') {
      return PrimengTableService.returnVariableTooltip(dynamicValue, this.dynamicRequirements.translateService);
    }
    return dynamicValue.label;
  }

  /**
   * Emit the event of checking a box on a column
   *
   * @param value
   */
  emitCheckCell(value: PrimengCheckColumnModel) {
    this.primengTableService.setCheckCellEvent(value);
  }

  /**
   * Emit the event of a data change
   *
   * @param value
   */
  changeDataEvent(value: any) {
    this.primengTableService.setChangeDataEvent(value);
  }

  /**
   * Emit the delete dynamic column event
   *
   * @param value Value of the dynamic column
   */
  deleteColumn(value: any) {
    this.primengTableService.setDeleteColumnEvent(value);
  }

  /**
   * Broadcast first page
   */
  broadCastFirstPage() {
    if (this.table && this.lazy) {
      const page = this.table.last.dataToRender;
      this.primengTableService.setCurrentPageEvent(page);
    }
  }

  /**
   * Apply text filter only when two date are selected and close the filter menu
   *
   * @param inputForm the form which is linked to the filter input
   * @param filter
   * @param dataTableFilter
   */
  applyTextFilterValue(inputForm: AbstractControl, filter, dataTableFilter): void {
    filter(inputForm.value || null);
    dataTableFilter.overlayVisible = false;
    dataTableFilter.dt.cd.markForCheck();
  }

  clearTextFilter(dataTableFilter) {
    dataTableFilter.clearFilter() ;
    dataTableFilter.overlayVisible = false;
  }

  /**
   * Extract default sort from the cols list configuration
   * And create the metaSortData list to active the default sort on the primeng table
   */
  constructDefaultSort() {
    const cols = [... this.cols];
    // Sort the col list to reorder by defaultSortPosition
    cols.sort((cola: PrimengTableCols, colb: PrimengTableCols) => {
      if (cola.defaultSortPosition > colb.defaultSortPosition) {
        return 1;
      }
      return -1;
    });
    const multiSortMeta: SortMeta[] = [];
    // Create the multiSortMeta list for the table
    cols.forEach((col: PrimengTableCols) => {
      if (col.defaultSortOrder && col.defaultSortPosition) {
        multiSortMeta.push({field: col.field, order: col.defaultSortOrder});
      }
    });
    this.multiSortMeta = (multiSortMeta.length > 0) ? multiSortMeta : null;
  }

  /**
   * return the text to display for a given col and data
   * @param col     the col where the text must be displayed
   * @param rowData the data that contains the field to display
   * @returns the text to display for the row and col
   */
  returnColDataToDisplay(col: PrimengTableCols, rowData: any): any {
    const rawColData = PrimengTableService.returnRawRowDataToDisplay(col, rowData) ;
    if (FunctionUtils.isNotDefined(rawColData)) {
      return '';
    }
    const mappedStr = col.lookupValueTable?.get(rawColData);
    return FunctionUtils.isNotDefined(mappedStr) ? rawColData : mappedStr;

  }

  /**
   * Check if the col is sortable or not
   * @param col tje col that you want to check if it is sortable or not
   * @returns true if sortable
   */
  isSortable(col: PrimengTableCols): boolean {
    return !col.dynamicValue && col.sortable !== false && !this.isColForExtendedAction(col) ;
  }

  /**
   * Return the right conf for the filter
   * @param col       the col to filter
   * @param confType  the type of the conf to get
   */
  returnFilterConf(col: PrimengTableCols, confType: 'type' | 'matchMode') {
    PrimengTableService.returnFilterConf(col, confType);
  }



  /**
   * Disable the check all box of import and override col on the variable import page.
   *
   * @param col
   */
  isImportOrOverrideAllEnabled(col: PrimengTableCols): boolean {
    switch(col.field) {
      case 'overwritten':
        return col.importPreview?.predictionModels.some(predMod => predMod.overwritten > 0);
      case 'imported':
        return col.importPreview?.predictionModels.some(predMod => predMod.imported > 0);
      default:
        return true;
    }
  }

  /**
   * Return the style of a column for <td> tag
   * @param col the column to set the style
   * @returns
   */
  returnTdColStyle(col: PrimengTableCols): {[styleName: string]: string} {
    /* If we impose the minimum width then the width has to be set to 0px
     * to let the column to feat with its largest content or its header */
    return col?.imposeMinWidth ? { 'width': '0px' } : { }
  }

  /**
   * Return the style of a column for <th> tag
   * @param col the column to set the style
   * @returns
   */
  returnThColStyle(col: PrimengTableCols): {[styleName: string]: string} {
    const isInput = col?.type?.includes('input') ;
    /* let a padding to impose min space between the end of header and next column
     * when input mode AND imposed minimum width */
    return col?.imposeMinWidth && isInput ? { 'padding-right': '20px' } : { }
  }

  regularSort(event: SortEvent): void {
    event.data.sort((data1, data2) => {
      let value1 = data1[event.field];
      let value2 = data2[event.field];
      let result = null;

      if (value1 == null && value2 != null)
        result = -1;
      else if (value1 != null && value2 == null)
        result = 1;
      else if (value1 == null && value2 == null)
        result = 0;
      else if (typeof value1 === 'string' && typeof value2 === 'string')
        result = value1.localeCompare(value2);
      else if (value1 < value2)
        result = -1;
      else if (value1 > value2)
        result = 1;
      else
        result = 0;
      return (event.order * result);
    });
  }

  /**
   * Return the cell class
   *
   * @param rowData the data of the row
   * @returns the class to add to the cell
   */
  returnCellClass(col: PrimengTableCols, rowData: PrimengTableCols): string {
    const cellClassVal = col.returnCellClasses?.(rowData) ;
    return `${this.returnCellDataPositionClass(col.position)} ${cellClassVal?.join(' ') || ''}`;
  }

  /**
   * Return the effective field name
   *
   * @param col the col from what the the field name must be from
   * @returns the effective field name
   */
  returnEffectiveFieldName(col: PrimengTableCols): string {
    return col.forcedFieldIdentifier ?? col.field;
  }

  getFilterId(col: PrimengTableCols): string {
    let filterId: string = "FilterInput_" + col.field;
    if (col.fieldLvl2) {
      filterId = filterId + "_" + col.fieldLvl2;
    }
    return filterId;
  }

  onShowFilter(col: PrimengTableCols, firstTry: boolean = true) {
    const element: HTMLElement = document.getElementById(this.getFilterId(col));
    if (element) {
      element.focus();
    } else if (firstTry) {
      /* In case the element is not shown at first */
      setTimeout(() => this.onShowFilter(col, false), 500)
    }
  }

  /**
   * Display and change value of all row
   * @param colum the column
   */
  massEditColumn(colum: PrimengTableCols) {
    // Defined option for select
    let options = undefined;

    if (colum.type === "select") {
      options = colum.options;
    } else if (colum.type === "input-nutrient") {
      options = colum.header;
    }

    // Call editing pop-in
    this.dialogService.showEditionPopin(colum.field, colum.type, options).afterClosed().subscribe(result => {
      if (result) {
        // If it's entity or reference
        if (colum.field === 'entityLabel' || colum.field === 'referenceLabel') {
          const option = colum.options.find(c => c.id == result.response.id);

          // Set new entity
          if (colum.field === 'entityLabel') {
            const urnEntity = new URN();
            urnEntity.identifier = option.code;
            this.dataRows.forEach(rm => {
              rm.entity = urnEntity;
              rm.entityLabel = option.name
            });
          // Set new reference
          } else {
            this.dataRows.forEach(rm => {
              rm.referenceId = option.id;
              rm.referenceLabel = option.label;
            });
          }
        // If it's a nutrient
        } else if (colum.type === 'input-nutrient') {
          // Change nutrient value
          for (const [_, value] of Object.entries(this.nutrientsData)) {
            let nutrient = value.find(n => n.nutrientCode == colum.field);
            if (nutrient)
              nutrient.value = result.response;
          }
        // If it's to add a the end (notes)
        } else if (result.isAddToFollow) {
          this.dataRows.forEach(rm => {
            if (rm[colum.field] == null || rm[colum.field] == '') {
              rm[colum.field] = result.response;
            } else {
              rm[colum.field] += '\n' + result.response;
            }
          });
        } else {
          this.dataRows.forEach(rm => rm[colum.field] = result.response);
        }
        // Emit change Event
        const fieldMap: Record<string, string> = {
          referenceLabel: "forcedReference",
          entityLabel: "forcedEntity"
        };
        this.changeDataEvent(fieldMap[colum.field] ?? result);
      }
    });
  }
}
